import { useDispatch, useStore } from 'react-redux';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router';

import {
  AppState,
  putSupportProviderSetForClientByClientIdRequest,
  selectClientSupportProviderSet,
} from '@/model';
import { useIsClientPlanReadOnly } from '@/shared/hooks/use-active-pilot-program';

export function useSupportProviderSyncOnExit() {
  const dispatch = useDispatch();
  const store = useStore<AppState>();
  const location = useLocation();
  const navigationType = useNavigationType();
  const isClientPlanReadOnly = useIsClientPlanReadOnly();

  const sentRef = useRef(false);

  const flush = useCallback(() => {
    if (isClientPlanReadOnly) return;

    const supportProviderSet = selectClientSupportProviderSet(store.getState());
    if (!supportProviderSet) return;

    dispatch(
      putSupportProviderSetForClientByClientIdRequest({
        supportProviderSet,
      }),
    );
  }, [dispatch, store, isClientPlanReadOnly]);

  const flushOnce = useCallback(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    flush();
  }, [flush]);

  useEffect(() => {
    if (navigationType === 'POP') {
      flushOnce();
    }
  }, [location, navigationType, flushOnce]);

  useEffect(() => {
    const onPageHide = () => flushOnce();
    const onBeforeUnload = () => flushOnce();

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [flushOnce]);

  return { flush };
}
